import React, { ReactNode, useMemo } from 'react';
import clsx from 'clsx';
import { TEST_IDS } from 'shared';
import { useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, ButtonGroup, Tooltip } from '@mui/material';
import { Menu } from './Menu';
import { Finance } from './finance/Finance';
import { useSaleMode } from '../../../../../context';
import { deskingSelectors } from '../../../../../model';
import { FormSectionHeader } from '../../../../components';
import {
  SaleType,
  FormSections,
  FormSectionProps,
  SaleStructureType,
  FinanceProgramFormValues,
} from '../../../../../types';

export const Structure = ({
  sectionName,
  createProductSlot,
  formFieldsConfiguration,
  formDefaultValues,
  disableFinanceStructure,
  onClearStructureAndMenuOptions,
}: FormSectionProps & {
  createProductSlot?: ReactNode;
  disableFinanceStructure: boolean;
  formDefaultValues?: FinanceProgramFormValues;
}) => {
  const classes = useStyles();

  const { saleStructureType, onChangeStructureType } = useSaleMode();

  const isSectionOpen = useSelector((state: any) => deskingSelectors.isSectionOpen(state, sectionName));
  const isSectionCollapsed = useSelector((state: any) => deskingSelectors.isSectionCollapsed(state, sectionName));
  const isBuilderSectionCollapsed = useSelector((state: any) =>
    deskingSelectors.isSectionCollapsed(state, FormSections.MenuBuilder)
  );
  const isFinanceDetailsEditable = useSelector(deskingSelectors.selectFinanceDetailsEditableStatus);

  const { control } = useFormContext<FinanceProgramFormValues>();
  const [financeQuote, saleDetails] = useWatch({ control, name: ['financeQuote', 'saleDetails'] });

  const isMenuSectionDisabled = useMemo(() => {
    if (saleDetails.saleType === SaleType.Cash) return false;

    const isFinanceProgramSelected = financeQuote.createProposalWithExistingProgram !== null;
    const isCalculationDepsExists = !!saleDetails.financeAmount || !!financeQuote.terms.length;

    return !isFinanceProgramSelected || !isCalculationDepsExists;
  }, [financeQuote, saleDetails]);

  const renderNestedSectionByMode: Record<SaleStructureType, ReactNode> = {
    [SaleStructureType.Finance]: (
      <Finance configuration={formFieldsConfiguration} formDefaultValues={formDefaultValues} />
    ),
    [SaleStructureType.AftermarketMenu]: <Menu createProductSlot={createProductSlot} />,
  };

  return (
    <Box className={clsx('section', !isBuilderSectionCollapsed && 'sectionHidden')}>
      <FormSectionHeader
        name="Structure"
        sectionName={sectionName}
        isSectionOpen={isSectionOpen}
        isSectionCollapsed={isSectionCollapsed}
        extraAction={
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={onClearStructureAndMenuOptions}
            disabled={!isFinanceDetailsEditable}
            data-test-id={TEST_IDS.desking.structure.resetBtn}
          >
            Reset
          </Button>
        }
      />

      <Box className="sectionFields">
        <ButtonGroup fullWidth>
          <Button
            color="primary"
            disabled={disableFinanceStructure}
            variant={saleStructureType === SaleStructureType.AftermarketMenu ? 'outlined' : 'contained'}
            onClick={() => {
              onChangeStructureType(SaleStructureType.Finance);
            }}
            data-test-id={TEST_IDS.desking.structure.financeBtn}
          >
            Finance
          </Button>

          <Tooltip
            arrow
            disableHoverListener={!isMenuSectionDisabled}
            title="Please fill in Terms in Months and Buy Rate section first to access the Menu section"
          >
            <span className={classes.disableTooltipWrapper}>
              <Button
                color="primary"
                disabled={isMenuSectionDisabled || !isFinanceDetailsEditable}
                variant={saleStructureType === SaleStructureType.Finance ? 'outlined' : 'contained'}
                onClick={() => {
                  onChangeStructureType(SaleStructureType.AftermarketMenu);
                }}
                data-test-id={TEST_IDS.desking.structure.menuBtn}
              >
                Menu
              </Button>
            </span>
          </Tooltip>
        </ButtonGroup>

        <Box className="sectionFields">{renderNestedSectionByMode[saleStructureType]}</Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  disableTooltipWrapper: {
    width: '100%',
    display: 'block',

    '& > button': {
      width: '100%',
    },
  },
});
